import React, { useState } from 'react';
import {
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Icon,
  IconButton,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { format } from 'date-fns';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { quoteStatuses } from '../helpers';


const FilterDrawer = ({setOpenFilter, setFilterConditions, setCurrentPage, filterConditions}: IProgressionFilterDrawerProps) => {
  const [expandedStatus, toggleExpandedStatus] = useState(false);
  const [expandedDate, toggleExpandedDate] = useState(false);

  const [conditions, setConditions] = useState({
    stage: filterConditions.stage || '',
    startDate: filterConditions.startDate || '',
    endDate: filterConditions.endDate || '',
  });

  const handleChangeFilter = (field, value) => {
    setConditions((conditions) => (
      {
        ...conditions,
        [field]: value
      }
    ));
  };

  const handleApplyFilter = () => {
    const formattedConditions = { ...conditions };

    if (conditions.startDate) {
      const startDate = new Date(conditions.startDate);
      formattedConditions.startDate = `${format(startDate, 'yyyy-MM-dd')} 00:00:00`;
    }

    if (conditions.endDate) {
      const endDate = new Date(conditions.endDate);
      formattedConditions.endDate = `${format(endDate, 'yyyy-MM-dd')} 23:59:59`;
    }

    const filteredConditions = Object.keys(formattedConditions).reduce((acc, key) => {
      if (formattedConditions[key]) {
        acc[key] = formattedConditions[key];
      }

      return acc;
    }, {});

    setFilterConditions(filteredConditions);
    setCurrentPage(1);
    setOpenFilter(false);
  };

  const handleChangeDate = (field, date) => {
    setConditions((prevConditions) => ({
      ...prevConditions,
      [field]: date ? format(date, 'MM/dd/yyyy') : ''
    }));
  };

  const handleChangePeriod = (days) => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate());
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - days);

    setConditions((prevConditions) => ({
      ...prevConditions,
      startDate: format(startDate, 'MM/dd/yyyy'),
      endDate: format(endDate, 'MM/dd/yyyy')
    }));
  };

  const isStartDateRequired = conditions.endDate && !conditions.startDate;
  const isEndDateRequired = conditions.startDate && !conditions.endDate;
  const isApplyButtonEnabled = (conditions.startDate || conditions.endDate) ? (new Date(conditions.endDate) >= new Date(conditions.startDate)) : true;

  return (
    <div className="px-4 pt-4 min-h-full-screen flex-column justify-between">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Typography variant="h5" className="text-primary uppercase">Filter by:</Typography>
          <IconButton onClick={() => setOpenFilter(false)}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </div>
        <div className="flex items-center flex-wrap mb-2">
          {Object.keys(conditions).map((filterItemKey, index) => {
            if (!conditions[filterItemKey]) {
              return null;
            }

            let filterLabel = conditions[filterItemKey];

            if (filterItemKey === 'stage') {
              filterLabel = quoteStatuses.find((el) => el.value === Number(conditions[filterItemKey]))?.name || '';
            }


            if (filterItemKey === 'startDate') {
              filterLabel = `Created from: ${conditions[filterItemKey]}`;
            }

            if (filterItemKey === 'endDate') {
              filterLabel = `Created to: ${conditions[filterItemKey]}`;
            }

            return (
              <div key={`filter-${index}`} className="mr-2 mb-2">
                <Chip
                  label={filterLabel}
                  onDelete={() => {
                    setConditions({
                      ...conditions,
                      [filterItemKey]: ''
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedStatus((expandedStatus) => !expandedStatus)}
        >
          <Typography variant="h6">Status</Typography>
          {expandedStatus ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedStatus} timeout="auto">
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="stage"
              name="stage"
              value={String(conditions?.stage || '')}
              onChange={({target: {value, name}}) => {
                handleChangeFilter(name, value);
              }}
            >
              {quoteStatuses.map((el) => (
                <FormControlLabel
                  key={`status-${el.value}`}
                  value={String(el.value)}
                  control={<Radio color="primary" size="small" />}
                  label={el.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedDate((expandedDate) => !expandedDate)}
        >
          <Typography variant="h6">Date Quote Created</Typography>
          {expandedDate ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedDate} timeout="auto">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              className="mb-4 mr-5"
              variant="inline"
              label="Start Date"
              autoOk
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              value={conditions?.startDate || null}
              onChange={(date) => {
                handleChangeDate('startDate', date);
              }}
              disableFuture
              error={isStartDateRequired}
              helperText={isStartDateRequired && 'Please select a Start Date'}
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              className="mb-4 mr-5"
              variant="inline"
              label="End Date"
              autoOk
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              value={conditions?.endDate || null}
              onChange={(date) => {
                handleChangeDate('endDate', date);
              }}
              disableFuture
              error={
                (conditions.startDate && conditions.endDate && new Date(conditions.endDate) < new Date(conditions.startDate)) ||
                isEndDateRequired
              }
              helperText={
                ((conditions.startDate && !conditions.endDate && isEndDateRequired) && 'Please select a End Date')
                ||
                ((conditions.startDate && conditions.endDate && new Date(conditions.endDate) < new Date(conditions.startDate)) &&
                'End Date cannot be less than Start Date')
              }
            />
          </MuiPickersUtilsProvider>
          <div className="mb-5">
            <Chip className="mr-2" size="small" label="1 Year" onClick={() => handleChangePeriod(365)} />
            <Chip className="mr-2" size="small" label="3 Months" onClick={() => handleChangePeriod(90)} />
            <Chip className="mr-2" size="small" label="1 Month" onClick={() => handleChangePeriod(30)} />
            <Chip className="mr-2" size="small" label="1 Week" onClick={() => handleChangePeriod(7)} />
            <Chip className="mr-2" size="small" label="1 Day" onClick={() => handleChangePeriod(1)} />
          </div>
        </Collapse>
        <Divider className="my-5" />
        <div className="flex items-center justify-between pb-4">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className="mr-2 uppercase"
            disabled={!isApplyButtonEnabled}
            onClick={handleApplyFilter}
          >
            Apply Filters
          </Button>
          <Button
            variant="outlined"
            fullWidth
            className="uppercase"
            onClick={() => {
              setFilterConditions({});
              setOpenFilter(false);
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterDrawer;
